import React, { useState } from "react";
import { Input, Select, SelectOption, Text, Box, Separator, Button } from "@showitinc/showit-wc";
import { toASCII, toUnicode, isValidDomain, stripProtocolAndPath } from "../../../utils/DomainUtils";
import InputSanitizer from "../../../utils/InputSanitizer";
import ViewPanel from "../../blogsetup/ViewPanel";
import SetupButtonBar from "../../blogsetup/SetupButtonBar";
import Constants from "../Constants";
import useOpenAccount, { PATH_DOMAINS } from "../../../hooks/useOpenAccount";
import styles from "./DomainInfo.module.css";

const { containsShowit } = InputSanitizer;

function DomainInfo({ blogSetup, actions }) {
  const { domainConfig, path, isDomainSubmitError, domainSubmitError } = blogSetup;
  const [openAccountProfile] = useOpenAccount({ path: PATH_DOMAINS, windowTarget: "_blank" });
  const [validationError, setValidationError] = useState(null);
  const isAdvancedBlog = path === Constants.PATH_ADV_NEW || path === Constants.PATH_ADV_MIGRATE;

  const ownDomain = domainConfig.owns_domain ? "Y" : "N";
  const domainName = toUnicode(domainConfig.domain_name || "");

  const proceed = () => {
    if (ownDomain === "N" || !domainName) {
      return actions.close();
    }

    if (containsShowit(domainName)) {
      return setValidationError("Please do not use Showit in your domain name.");
    }

    if (!isValidDomain(domainName)) {
      return setValidationError("Please enter a valid domain.");
    }

    const asciiDomain = toASCII(stripProtocolAndPath(domainName));
    actions.updateDomain({ domain_name: asciiDomain });

    if (isAdvancedBlog) {
      actions.goToNextStep();
    } else {
      const extraConfig =
        path === Constants.PATH_BASIC_BLOG
          ? { configure_domain: Constants.CONFIGURE_DOMAIN_NEW }
          : {};
      actions.submitDomainSetup(extraConfig);
    }
  };

  const updateOwnDomain = (val) => {
    if (validationError) setValidationError(null);
    actions.updateDomain({
      owns_domain: val === "Y",
      ...(val === "N" && { domain_name: "" }),
    });
  };

  const updateDomainName = ({ target }) => {
    const value = toASCII(target.value.toLowerCase().trim());
    if (validationError) setValidationError(null);
    actions.updateDomain({ domain_name: value });
  };

  const cleanDomainName = ({ target }) => {
    const val = toASCII(target.value.toLowerCase().trim());
    actions.updateDomain({ domain_name: val.replace("www.", "") });
  };

  const forwardBtnLabel = domainName?.length > 0 ? "Next" : "Done";
  const apiErrorMessage = isDomainSubmitError
    ? domainSubmitError?.message || "A problem occurred while setting up your domain."
    : null;
  const errorMessage = validationError || apiErrorMessage;

  let choiceOption = null;
  if (ownDomain === "Y") {
    choiceOption = (
      <Input
        className={styles["domain-name-input"]}
        fullWidth
        longLabel
        label="Domain Name"
        error={!!errorMessage}
        errorText={errorMessage}
        value={domainName}
        onSIChange={updateDomainName}
        onSIBlur={cleanDomainName}
        emitOnBlur
      />
    );
  } else {
    choiceOption = (
      <Button onClick={openAccountProfile} lowercase="true" variant="outline">
        Get a domain here!
      </Button>
    );
  }

  return (
    <ViewPanel>
      {{
        content: (
          <div className={styles["domain-info-wrapper"]}>
            <div className={styles["domain-info-inner"]}>
              <Box mb="2">
                <Text variant="heading-1" variantStyle="light">
                  Domain Name Information
                </Text>
                <Separator />
              </Box>
              <Box mb="2">
                <Text variant="body-2" mb="8">
                  Before you can publish to a custom domain we need to configure your domain.
                </Text>
              </Box>
              <Box mb="3">
                <Select
                  className={styles["own-domain-select"]}
                  name="own_domain"
                  label="Do you own a domain name? (i.e. example.com)"
                  longLabel
                  fullWidth
                  onSISelect={(e) => updateOwnDomain(e.target.value)}
                  value={ownDomain}
                >
                  <SelectOption value="Y">Yes</SelectOption>
                  <SelectOption value="N">No</SelectOption>
                </Select>
              </Box>
              {choiceOption}
            </div>
          </div>
        ),
        actions: (
          <SetupButtonBar
            onForward={proceed}
            forwardBtnText={forwardBtnLabel}
          />
        ),
      }}
    </ViewPanel>
  );
}

export default DomainInfo;
